import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, Search, CheckCircle, XCircle, ExternalLink, Eye, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Receipt viewer
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [viewingReceiptUrl, setViewingReceiptUrl] = useState('');
  const [loadingReceiptUrl, setLoadingReceiptUrl] = useState(false);

  // New in-page alerts & custom modal states
  const [notification, setNotification] = useState({ type: '', text: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [rejectionModal, setRejectionModal] = useState({ isOpen: false, payment: null, reason: '' });

  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => {
      setNotification(prev => prev.text === text ? { type: '', text: '' } : prev);
    }, 5000);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false });

      if (paymentsError) throw paymentsError;
      setPayments(paymentsData || []);

      const { data: usersData, error: usersError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, subscription_plan');

      if (usersError) throw usersError;

      const usersMap = {};
      usersData.forEach(u => { usersMap[u.id] = u; });
      setUsers(usersMap);
    } catch (err) {
      console.error('Error fetching payments data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatUserName = (u) => {
    if (!u) return 'Client';
    const fn = (u.first_name || '').trim();
    const ln = (u.last_name || '').trim();
    if (fn && fn !== 'null' && fn !== 'Unknown') {
      return `${fn} ${ln && ln !== 'null' ? ln : ''}`.trim();
    }
    if (u.email) {
      const prefix = u.email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return 'Client';
  };

  // Get plan name from payment amount
  const getPlanFromAmount = (amount) => {
    if (amount >= 140000) return 'premium';
    if (amount >= 90000) return 'standard';
    return 'basic';
  };

  const handleViewReceipt = async (payment) => {
    setViewingReceipt(payment);
    setViewingReceiptUrl('');
    setLoadingReceiptUrl(true);

    if (payment.receipt_url) {
      if (payment.receipt_url.startsWith('http')) {
        setViewingReceiptUrl(payment.receipt_url);
        setLoadingReceiptUrl(false);
      } else {
        try {
          const { data, error } = await supabase.storage
            .from('documents')
            .createSignedUrl(payment.receipt_url, 3600);

          if (error) throw error;
          setViewingReceiptUrl(data.signedUrl);
        } catch (err) {
          console.error('Error creating signed URL for receipt:', err);
          // Fallback
          setViewingReceiptUrl(payment.receipt_url);
        } finally {
          setLoadingReceiptUrl(false);
        }
      }
    } else {
      setLoadingReceiptUrl(false);
    }
  };

  const handleApprovePayment = async (payment) => {
    const plan = payment.plan || getPlanFromAmount(payment.amount);
    const isPlacementFee = plan === 'placement_fee';
    
    setConfirmModal({
      isOpen: true,
      title: isPlacementFee ? 'Approve Placement Fee' : 'Approve Payment',
      message: isPlacementFee 
        ? `Are you sure you want to approve this placement fee payment of ₦${payment.amount?.toLocaleString()}? (This will not change the client's current subscription plan).`
        : `Are you sure you want to approve this payment of ₦${payment.amount?.toLocaleString()} and activate the ${plan} plan for this client?`,
      onConfirm: async () => {
        try {
          // 1. Update Payment Status
          const { error: paymentError } = await supabase
            .from('payments')
            .update({ status: 'successful' })
            .eq('id', payment.id);

          if (paymentError) throw paymentError;

          // 2. Update User Profile - set plan and status to active (only if NOT placement fee)
          if (!isPlacementFee) {
            // Fetch current profile to get current subscription_expiry
            const { data: userProfile } = await supabase
              .from('profiles')
              .select('subscription_expiry')
              .eq('id', payment.user_id)
              .single();

            let baseDate = new Date();
            if (userProfile?.subscription_expiry) {
              const currentExpiry = new Date(userProfile.subscription_expiry);
              if (currentExpiry > baseDate) {
                baseDate = currentExpiry;
              }
            }

            const newExpiry = new Date(baseDate);
            newExpiry.setMonth(newExpiry.getMonth() + 1);

            const isInitialInstallment = payment.notes?.includes('Initial 70%');
            const isFinalInstallment = payment.notes?.includes('Final 30%');
            const profileUpdates = { 
              subscription_status: 'active',
              subscription_plan: plan,
              subscription_expiry: newExpiry.toISOString(),
              subscription_renewal_date: newExpiry.toISOString(),
              expiry_warning_sent: false,
              expiry_email_sent: false
            };

            if (isInitialInstallment) {
              profileUpdates.subscription_payment_type = 'installment';
            } else if (!isFinalInstallment) {
              profileUpdates.subscription_payment_type = 'full';
            }

            const { error: profileError } = await supabase
              .from('profiles')
              .update(profileUpdates)
              .eq('id', payment.user_id);
            
            if (profileError) throw profileError;
          }

          // 3. Send a confirmation message to the user
          const messageText = isPlacementFee 
            ? `✅ Placement Fee Payment Confirmed!\n\nYour placement fee payment of ₦${payment.amount?.toLocaleString()} has been verified. Thank you for your payment! 🚀`
            : `✅ Payment Confirmed!\n\nYour payment of ₦${payment.amount?.toLocaleString()} has been verified and your ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan is now active.\n\nYou can now upload your CV and we'll start sending strategic applications on your behalf. Welcome aboard! 🚀`;

          await supabase.from('messages').insert({
            user_id: payment.user_id,
            message: messageText,
            is_from_admin: true,
            admin_has_replied: true,
          });

          // 3.5 Send Real Email
          try {
            await supabase.functions.invoke('send-email', {
              body: { 
                user_id: payment.user_id, 
                subject: isPlacementFee ? 'Placement Fee Payment Confirmed! 🎉' : 'Payment Confirmed - Plan Activated! 🎉', 
                html: isPlacementFee ? `
                  <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
                    <h2 style="color: #4F46E5;">Placement Fee Payment Confirmed!</h2>
                    <p>Hello,</p>
                    <p>Your recent payment of <strong>₦${payment.amount?.toLocaleString()}</strong> for the placement fee has been successfully verified by our team.</p>
                    <p>Thank you for your payment and support!</p>
                    <br/>
                    <p>Best regards,<br/>The Placement Point Team</p>
                  </div>
                ` : `
                  <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; color: #334155;">
                    <h2 style="color: #4F46E5;">Payment Successful!</h2>
                    <p>Hello,</p>
                    <p>Your recent bank transfer of <strong>₦${payment.amount?.toLocaleString()}</strong> has been successfully verified by our team.</p>
                    <p>Your account has been upgraded to the <strong>${plan.charAt(0).toUpperCase() + plan.slice(1)}</strong> plan. You now have access to all the premium features included in your subscription!</p>
                    <p>You can now upload your CV in the dashboard, and we'll start sending strategic applications on your behalf.</p>
                    <br/>
                    <p>Best regards,<br/>The Placement Point Team</p>
                  </div>
                `
              }
            });
          } catch (emailErr) {
            console.error('Failed to send email:', emailErr);
          }

          // Send push notification to user
          supabase.functions.invoke('send-push', {
            body: {
              user_id: payment.user_id,
              title: isPlacementFee ? 'Placement Fee Confirmed! 🎉' : 'Payment Confirmed! 🎉',
              body: isPlacementFee 
                ? `Your placement fee payment of ₦${payment.amount?.toLocaleString()} has been verified.`
                : `Your payment of ₦${payment.amount?.toLocaleString()} has been verified and your plan is active.`,
              url: '/dashboard'
            }
          }).catch(e => console.error('Error triggering push:', e));

          // 4. Update local state
          setPayments(payments.map(p => p.id === payment.id ? { ...p, status: 'successful' } : p));
          if (!isPlacementFee) {
            setUsers(prev => ({
              ...prev,
              [payment.user_id]: { ...prev[payment.user_id], subscription_plan: plan }
            }));
          }
          
          showNotification('success', isPlacementFee ? 'Placement fee payment approved!' : `Payment approved! ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan activated for client.`);
        } catch (err) {
          console.error('Error approving payment:', err);
          showNotification('error', 'Failed to approve payment.');
        }
      }
    });
  };

  const handleRejectPayment = (payment) => {
    setRejectionModal({
      isOpen: true,
      payment,
      reason: ''
    });
  };

  const submitRejection = async () => {
    const { payment, reason } = rejectionModal;
    if (!payment) return;

    setRejectionModal({ isOpen: false, payment: null, reason: '' });

    try {
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ 
          status: 'failed',
          notes: reason // Store rejection reason under the notes column
        })
        .eq('id', payment.id);

      if (paymentError) throw paymentError;

      // Send rejection message to user
      await supabase.from('messages').insert({
        user_id: payment.user_id,
        message: `❌ Payment Not Verified\n\nWe were unable to verify your payment of ₦${payment.amount?.toLocaleString()}.${reason ? `\n\nReason: ${reason}` : ''}\n\nPlease re-upload your receipt or contact us if you believe this is an error.`,
        is_from_admin: true,
        admin_has_replied: true,
      });

      setPayments(payments.map(p => p.id === payment.id ? { ...p, status: 'failed', notes: reason } : p));
      showNotification('success', 'Payment marked as failed. User has been notified.');
    } catch (err) {
      console.error('Error rejecting payment:', err);
      showNotification('error', 'Failed to reject payment.');
    }
  };

  const pendingCount = payments.filter(p => p.status === 'pending').length;
  const statuses = ['all', 'pending', 'successful', 'failed'];

  const filteredPayments = payments.filter(p => {
    const user = users[p.user_id];
    const searchStr = `${user?.first_name} ${user?.last_name} ${p.status} ${p.plan || ''} ${p.type || ''}`.toLowerCase();
    const matchesSearch = searchStr.includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-indigo-950">
            Payment Verification
            {pendingCount > 0 && (
              <span className="ml-3 inline-flex items-center justify-center w-7 h-7 bg-amber-500 text-white text-sm font-bold rounded-full">
                {pendingCount}
              </span>
            )}
          </h1>
          <p className="mt-1 text-slate-500">Verify bank transfers, view receipts, and activate subscriptions.</p>
        </div>
        
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search by client..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-indigo-500 capitalize"
          >
            {statuses.map(s => (
              <option key={s} value={s}>{s === 'all' ? 'All Status' : s}</option>
            ))}
          </select>
        </div>
      </div>

      {notification.text && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-3 transition-all duration-300 animate-fadeIn ${
            notification.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1 text-sm font-medium pr-8 leading-relaxed">{notification.text}</div>
          <button
            onClick={() => setNotification({ type: '', text: '' })}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0 focus:outline-none transition-colors"
          >
            <span className="text-xl font-bold leading-none">&times;</span>
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                  <th className="p-4">Date</th>
                  <th className="p-4">Client</th>
                  <th className="p-4">Type</th>
                  <th className="p-4">Amount</th>
                  <th className="p-4">Plan</th>
                  <th className="p-4">Receipt</th>
                  <th className="p-4">Admin Note / Reason</th>
                  <th className="p-4 text-center">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredPayments.map((payment) => {
                  const user = users[payment.user_id];
                  const plan = payment.plan || getPlanFromAmount(payment.amount);
                  return (
                    <tr key={payment.id} className={`hover:bg-slate-50 transition-colors ${payment.status === 'pending' ? 'bg-amber-50/30' : ''}`}>
                      <td className="p-4 text-slate-500 whitespace-nowrap">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 font-semibold text-slate-900 whitespace-nowrap">
                        {formatUserName(user)}
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          payment.type === 'flutterwave' ? 'bg-emerald-50 text-emerald-700 border border-emerald-250' :
                          payment.type === 'placement_fee' ? 'bg-blue-50 text-blue-705 border border-blue-250' :
                          'bg-indigo-50 text-indigo-707 border border-indigo-250'
                        }`}>
                          {payment.type === 'bank_transfer' ? 'Bank Transfer' : payment.type === 'flutterwave' ? 'Flutterwave' : payment.type || 'Bank Transfer'}
                        </span>
                      </td>
                      <td className="p-4 font-bold text-slate-900 whitespace-nowrap">
                        ₦{payment.amount?.toLocaleString()}
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-indigo-100 text-indigo-700 capitalize">
                          {plan === 'placement_fee' ? 'Placement Fee' : plan}
                        </span>
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        {payment.receipt_url ? (
                          <button
                            onClick={() => handleViewReceipt(payment)}
                            className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 font-medium text-xs"
                          >
                            <Eye className="w-4 h-4" />
                            View Receipt
                          </button>
                        ) : (
                          <span className="text-slate-400 text-xs">No receipt</span>
                        )}
                      </td>
                      <td className="p-4 text-slate-600 text-xs max-w-xs truncate" title={payment.notes || ''}>
                        {payment.notes || <span className="text-slate-300">-</span>}
                      </td>
                      <td className="p-4 whitespace-nowrap text-center">
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          payment.status === 'successful' ? 'bg-green-100 text-green-700' :
                          payment.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        {payment.status === 'pending' && (
                          <div className="flex items-center justify-end gap-2">
                            <button 
                              onClick={() => handleApprovePayment(payment)}
                              className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-bold hover:bg-green-100 transition-colors"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => handleRejectPayment(payment)}
                              className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-bold hover:bg-red-100 transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                        {payment.status === 'successful' && (
                          <span className="text-xs text-green-600 font-bold">✓ Verified</span>
                        )}
                        {payment.status === 'failed' && (
                          <span className="text-xs text-red-500 font-bold">Rejected</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filteredPayments.length === 0 && (
                  <tr>
                    <td colSpan="9" className="p-8 text-center text-slate-500">
                      <CreditCard className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                      <p>No payments found.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Receipt Viewer Modal */}
      {viewingReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="text-lg font-bold text-slate-900">Payment Receipt</h3>
              <button onClick={() => setViewingReceipt(null)} className="text-slate-400 hover:text-slate-600 text-xl font-bold">&times;</button>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 rounded-xl p-4 mb-4 text-sm space-y-1">
                <p><strong>Client:</strong> {formatUserName(users[viewingReceipt.user_id])}</p>
                <p><strong>Amount:</strong> ₦{viewingReceipt.amount?.toLocaleString()}</p>
                <p><strong>Date:</strong> {new Date(viewingReceipt.created_at).toLocaleString()}</p>
              </div>

              {viewingReceipt.receipt_url && (
                <div className="rounded-xl overflow-hidden border border-slate-200 mb-4 min-h-[100px] flex items-center justify-center bg-slate-105 relative">
                  {loadingReceiptUrl ? (
                    <div className="flex flex-col items-center gap-2 py-10">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                      <span className="text-xs text-slate-400">Signing access token...</span>
                    </div>
                  ) : (
                    <img 
                      src={viewingReceiptUrl || viewingReceipt.receipt_url} 
                      alt="Payment receipt" 
                      className="w-full h-auto max-h-96 object-contain bg-slate-100"
                    />
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setViewingReceipt(null); }}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
                {viewingReceipt.status === 'pending' && (
                  <>
                    <button
                      onClick={() => { handleApprovePayment(viewingReceipt); setViewingReceipt(null); }}
                      className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700 transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => { handleRejectPayment(viewingReceipt); setViewingReceipt(null); }}
                      className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-bold text-sm hover:bg-red-700 transition-colors"
                    >
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3 text-indigo-900">
                <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                  <CreditCard className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">{confirmModal.title}</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                {confirmModal.message}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
                  }}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Rejection Reason Modal */}
      {rejectionModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3 text-rose-950">
                <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
                  <XCircle className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-lg">Reject Payment</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Please enter the reason for rejecting this payment. This note will be visible to the user on their dashboard.
              </p>
              <div className="mb-6">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Reason for rejection *</label>
                <textarea
                  value={rejectionModal.reason}
                  onChange={(e) => setRejectionModal(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g. The uploaded receipt is blurred, or the transaction code could not be verified."
                  rows={3}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 focus:border-rose-500 resize-none shadow-sm"
                  required
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectionModal({ isOpen: false, payment: null, reason: '' })}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRejection}
                  disabled={!rejectionModal.reason.trim()}
                  className="flex-1 px-4 py-2.5 bg-rose-600 text-white rounded-xl font-bold text-sm hover:bg-rose-700 transition-colors disabled:opacity-50"
                >
                  Reject Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
